import { parseDate, roundToXDecimals } from '@ais-ui/utils';
import { QueryPointResult } from './extractTargetInfo';

export interface MethydTargetInfo {
  airPressureTendency: string;
  airPressure: string;
  airTemperature: string;
  averageWindSpeed: string;
  currentDirection2: string;
  currentDirection3: string;
  currentMeasuringLevel2: string;
  currentMeasuringLevel3: string;
  currentSpeed2: string;
  currentSpeed3: string;
  dewPoint: string;
  lastOrigin: string;
  location: string;
  message: string;
  metHydLatitude: string;
  metHydLongitude: string;
  latitude: number;
  longitude: number;
  metHydMmsi: string;
  metHydTimestamp: Date | null;
  relativeHumidity: string;
  reportTimestamp: Date | null;
  salinity: string;
  significantWaveHeight: string;
  surfaceCurrentDirection: string;
  surfaceCurrentSpeedIncludingTide: string;
  swellDirection: string;
  swellHeight: string;
  swellPeriod: string;
  waterLevelIncludingTide: string;
  waterLevelTrend: string;
  waterTemperature: string;
  waveDirection: string;
  wavePeriod: string;
  windDirection: string;
  windGustDirection: string;
  windGust: string;
}

const desiredTargetName: string[] = [
  'Air Pressure Tendency',
  'Air Pressure',
  'Air Temperature',
  'Average Wind Speed',
  'Current Direction #2',
  'Current Direction #3',
  'Current Measuring Level #2',
  'Current Measuring Level #3',
  'Current Speed #2',
  'Current Speed #3',
  'Dew Point',
  'Last origin',
  'Location',
  'Message',
  'MetHyd Latitude',
  'MetHyd Longitude',
  'MetHyd MMSI',
  'MetHyd timestamp',
  'Relative Humidity',
  'Report timestamp',
  'Salinity',
  'Significant Wave Height',
  'Surface Current Direction',
  'Surface Current Speed including tide',
  'Swell Direction',
  'Swell Height',
  'Swell Period',
  'Water Level including tide',
  'Water Level Trend',
  'Water Temperature',
  'Wave Direction',
  'Wave Period',
  'Wind Direction',
  'Wind Gust Direction',
  'Wind Gust',
];

const desiredTargetIds: string[] = [
  // New IDs
  'datetime_methyd_last_report',
  'datetime_methyd_message',
  'lat_methyd_last_report',
  'lon_methyd_last_report',
  'mmsi',
  'message_type',
  'avg_wind_speed',
  'wind_gust',
  'wind_direction',
  'wind_gust_direction',
  'lastorigin',
];

export function extractMethydTargetInfos(
  result: QueryPointResult
): MethydTargetInfo[] {
  const methydTargetInfos: MethydTargetInfo[] = [];
  for (const target of result.targetInfo) {
    const rawMethydTargetInfo: { [key: string]: string } = {};
    for (const target_value of target.values) {
      // Here we map the values by their name, since they all have same ID
      if (desiredTargetName.includes(target_value.name)) {
        rawMethydTargetInfo[target_value.name] = target_value.value;
      }
      // Also map by ID for new format
      if (desiredTargetIds.includes(target_value.id)) {
        rawMethydTargetInfo[target_value.id] = target_value.value;
      }
    }
    const mmsi =
      rawMethydTargetInfo['mmsi'] ||
      target['mmsi'] ||
      rawMethydTargetInfo['MetHyd MMSI'] ||
      rawMethydTargetInfo['MMSI'];
    const methydInfo: MethydTargetInfo = {
      airPressureTendency: rawMethydTargetInfo['Air Pressure Tendency'],
      airPressure: rawMethydTargetInfo['Air Pressure'],
      airTemperature: rawMethydTargetInfo['Air Temperature'],
      averageWindSpeed: rawMethydTargetInfo['avg_wind_speed'] ?? rawMethydTargetInfo['Average Wind Speed'],
      currentDirection2: rawMethydTargetInfo['Current Direction #2'],
      currentDirection3: rawMethydTargetInfo['Current Direction #3'],
      currentMeasuringLevel2: rawMethydTargetInfo['Current Measuring Level #2'],
      currentMeasuringLevel3: rawMethydTargetInfo['Current Measuring Level #3'],
      currentSpeed2: rawMethydTargetInfo['Current Speed #2'],
      currentSpeed3: rawMethydTargetInfo['Current Speed #3'],
      dewPoint: rawMethydTargetInfo['Dew Point'],
      lastOrigin: rawMethydTargetInfo['lastorigin'] ?? rawMethydTargetInfo['Last origin'],
      location: rawMethydTargetInfo['Location'],
      message: rawMethydTargetInfo['message_type'] ?? rawMethydTargetInfo['Message'],
      metHydLatitude: rawMethydTargetInfo['lat_methyd_last_report'] ?? rawMethydTargetInfo['MetHyd Latitude'],
      metHydLongitude: rawMethydTargetInfo['lon_methyd_last_report'] ?? rawMethydTargetInfo['MetHyd Longitude'],
      latitude: target['lat']
        ? roundToXDecimals(target['lat'], 4)
        : target['lat'],
      longitude: target['lon']
        ? roundToXDecimals(target['lon'], 4)
        : target['lon'],
      metHydMmsi: mmsi,
      metHydTimestamp: rawMethydTargetInfo['datetime_methyd_message'] ?? rawMethydTargetInfo['MetHyd timestamp']
        ? parseDate(rawMethydTargetInfo['datetime_methyd_message'] ?? rawMethydTargetInfo['MetHyd timestamp'])
        : null,
      relativeHumidity: rawMethydTargetInfo['Relative Humidity'],
      reportTimestamp: parseDate(rawMethydTargetInfo['datetime_methyd_last_report'] ?? rawMethydTargetInfo['Report timestamp']),
      salinity: rawMethydTargetInfo['Salinity'],
      significantWaveHeight: rawMethydTargetInfo['Significant Wave Height'],
      surfaceCurrentDirection: rawMethydTargetInfo['Surface Current Direction'],
      surfaceCurrentSpeedIncludingTide:
        rawMethydTargetInfo['Surface Current Speed including tide'],
      swellDirection: rawMethydTargetInfo['Swell Direction'],
      swellHeight: rawMethydTargetInfo['Swell Height'],
      swellPeriod: rawMethydTargetInfo['Swell Period'],
      waterLevelIncludingTide:
        rawMethydTargetInfo['Water Level including tide'],
      waterLevelTrend: rawMethydTargetInfo['Water Level Trend'],
      waterTemperature: rawMethydTargetInfo['Water Temperature'],
      waveDirection: rawMethydTargetInfo['Wave Direction'],
      wavePeriod: rawMethydTargetInfo['Wave Period'],
      windDirection: rawMethydTargetInfo['wind_direction'] ?? rawMethydTargetInfo['Wind Direction'],
      windGustDirection: rawMethydTargetInfo['wind_gust_direction'] ?? rawMethydTargetInfo['Wind Gust Direction'],
      windGust: rawMethydTargetInfo['wind_gust'] ?? rawMethydTargetInfo['Wind Gust'],
    };
    methydTargetInfos.push(methydInfo);
  }

  return methydTargetInfos;
}
